/**
 * dopamiles-header.js
 * Menu panel toggle + search expand + cart badge refresh for dopamiles-header.liquid.
 *
 * Responsibilities:
 *  - Hamburger → toggles slide-down menu panel inside <header>
 *  - ESC → closes whichever surface is open (menu or search)
 *  - Focus trap inside open menu panel
 *  - Search toggle button → expands search overlay, focuses input
 *  - Menu ↔ search mutex via 'bnc:menu:open' / 'bnc:search:open' custom events
 *  - Bag button → dispatches 'cart:open' (dopamiles-cart.js handles the rest)
 *
 *  Note: bag-count badge is owned by dopamiles-cart.js (see updateBubbleCount).
 *  This file dispatched a duplicate write in an earlier revision; removed to
 *  eliminate divergence at counts >= 100 ("99+" vs "100").
 */

(function () {
  'use strict';

  // ── Element references ──────────────────────────────────────────────────────

  const header        = document.getElementById('bnc-header');
  const hamburger     = document.getElementById('bnc-hamburger');
  const menuPanel     = document.getElementById('bnc-menu-panel');
  const bagBtn        = document.getElementById('bnc-bag-btn');
  const searchToggle  = document.getElementById('bnc-search-toggle');
  const searchOverlay = document.getElementById('bnc-search-overlay');
  const searchClose   = document.getElementById('bnc-search-overlay-close');
  const searchInput   = document.getElementById('bnc-header-search-input');

  // ── Header height tracker ──────────────────────────────────────────────────
  // The menu panel uses `top: var(--bnc-header-h)` to anchor below the sticky
  // header. We measure here (rather than hard-code) so the panel survives
  // logo-image swaps, font-load reflow, and viewport resizes.

  function updateHeaderHeight() {
    if (!header) return;
    // Use rect.bottom, not offsetHeight. The promo-bar block sits above the
    // (relatively-positioned) header in the same section group, so the visible
    // bottom edge of the sticky cluster = promo height + header height at
    // scroll=0, and just header height once scrolled past the promo. offsetHeight
    // would only measure the header element itself, leaving the panel anchor
    // 43px too high and clipping the first nav item behind the promo bar.
    const h = Math.max(0, header.getBoundingClientRect().bottom);
    if (h > 0) {
      document.documentElement.style.setProperty('--bnc-header-h', h + 'px');
    }
  }

  updateHeaderHeight();
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(updateHeaderHeight);
  }
  window.addEventListener('resize', updateHeaderHeight, { passive: true });
  // Promo bar scrolls away on page scroll → header.bottom shrinks. rAF-throttle
  // keeps the var fresh without firing per-pixel work.
  let scrollTick = false;
  window.addEventListener('scroll', () => {
    if (scrollTick) return;
    scrollTick = true;
    requestAnimationFrame(() => {
      updateHeaderHeight();
      scrollTick = false;
    });
  }, { passive: true });
  document.addEventListener('shopify:section:load', updateHeaderHeight);

  // ── Menu panel open / close ─────────────────────────────────────────────────

  function openMenu() {
    if (!menuPanel) return;
    // Re-measure header right before slide-down so the panel anchors to the
    // current sticky-cluster bottom (promo bar may have scrolled away since
    // page load).
    updateHeaderHeight();
    menuPanel.classList.add('is-open');
    menuPanel.setAttribute('aria-hidden', 'false');
    if (hamburger) {
      hamburger.setAttribute('aria-expanded', 'true');
      hamburger.setAttribute('aria-label', 'Close menu');
    }
    document.body.style.overflow = 'hidden';
    // Mutex: opening menu closes search overlay if it's up.
    document.dispatchEvent(new CustomEvent('bnc:menu:open'));
    // Focus first nav link so keyboard users land inside the panel.
    const firstFocusable = menuPanel.querySelector('a, button, [tabindex]:not([tabindex="-1"])');
    if (firstFocusable) firstFocusable.focus();
  }

  function closeMenu() {
    if (!menuPanel) return;
    menuPanel.classList.remove('is-open');
    menuPanel.setAttribute('aria-hidden', 'true');
    if (hamburger) {
      hamburger.setAttribute('aria-expanded', 'false');
      hamburger.setAttribute('aria-label', 'Open menu');
      hamburger.focus();
    }
    document.body.style.overflow = '';
  }

  function isMenuOpen() {
    return menuPanel && menuPanel.classList.contains('is-open');
  }

  // ── Focus trap (menu panel) ─────────────────────────────────────────────────

  function getFocusableEls() {
    if (!menuPanel) return [];
    return Array.from(
      menuPanel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
    ).filter((el) => !el.closest('[aria-hidden="true"]') && !el.closest('[hidden]'));
  }

  function trapFocus(e) {
    if (!isMenuOpen() || e.key !== 'Tab') return;

    const focusable = getFocusableEls();
    if (!focusable.length) return;

    const first = focusable[0];
    const last  = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // ── Sub-menu disclosure (menu panel) ────────────────────────────────────────
  // Delegated: submenu toggles only exist for menu items that have children.
  // State lives in the DOM (aria-expanded + [hidden]) so the focus trap's
  // [hidden] filter and assistive tech read the same truth the eye sees.

  if (menuPanel) {
    menuPanel.addEventListener('click', (e) => {
      const toggle = e.target.closest('.bnc-menu-sub-toggle');
      if (!toggle) return;
      const sub = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!sub) return;
      const isOpen = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!isOpen));
      sub.hidden = isOpen;
    });
  }

  // ── Search overlay ──────────────────────────────────────────────────────────
  // Toggles a full-width overlay panel that sits absolute over the header bar.

  function openSearch() {
    if (!searchOverlay) return;
    searchOverlay.classList.add('is-open');
    searchOverlay.setAttribute('aria-hidden', 'false');
    if (searchToggle) searchToggle.setAttribute('aria-expanded', 'true');
    // Mutex: opening search closes the menu panel if it's up.
    document.dispatchEvent(new CustomEvent('bnc:search:open'));
    // Focus after the next paint so the transition is allowed to start —
    // focusing mid-transition can suppress the screen-reader announce.
    if (searchInput) requestAnimationFrame(() => searchInput.focus());
  }

  function closeSearch() {
    if (!searchOverlay) return;
    searchOverlay.classList.remove('is-open');
    searchOverlay.setAttribute('aria-hidden', 'true');
    if (searchToggle) searchToggle.setAttribute('aria-expanded', 'false');
  }

  function isSearchOpen() {
    return searchOverlay && searchOverlay.classList.contains('is-open');
  }

  // ── Event wiring ────────────────────────────────────────────────────────────

  if (hamburger) {
    hamburger.addEventListener('click', () => {
      if (isMenuOpen()) {
        closeMenu();
      } else {
        openMenu();
      }
    });
  }

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (isMenuOpen()) {
        closeMenu();
        return;
      }
      if (isSearchOpen()) {
        closeSearch();
        if (searchToggle) searchToggle.focus();
      }
    }
    trapFocus(e);
  });

  if (searchToggle) {
    searchToggle.addEventListener('click', () => {
      if (isSearchOpen()) {
        closeSearch();
      } else {
        openSearch();
      }
    });
  }

  // Dedicated close button
  if (searchClose) {
    searchClose.addEventListener('click', () => {
      closeSearch();
      if (searchToggle) searchToggle.focus();
    });
  }

  // Close search on outside click. Clicking inside the overlay (input, submit,
  // close), the toggle, OR the predictive-panel sibling counts as "inside the
  // search context" and should NOT close the overlay. Without the predictive-
  // panel exemption, clicking a predictive result (which lives in a sibling
  // panel injected by dopamiles-search.js) closed the overlay mid-click before
  // the result link could resolve.
  document.addEventListener('click', (e) => {
    if (!isSearchOpen()) return;
    if (searchOverlay.contains(e.target)) return;
    if (searchToggle && searchToggle.contains(e.target)) return;
    const predictivePanel = document.getElementById('bnc-search-panel');
    if (predictivePanel && predictivePanel.contains(e.target)) return;
    closeSearch();
  });

  // Mutex listener: when search opens, close menu.
  document.addEventListener('bnc:search:open', () => {
    if (isMenuOpen()) closeMenu();
  });

  if (bagBtn) {
    bagBtn.addEventListener('click', () => {
      document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
    });
  }

})();
